import type { RailStepKey, RailTarget } from "../rail-model";
import { InlineLockIcon, LockedStepPreview } from "./shared";
import "./locked-step.css";

export function ClosedStep(props: {
  step: RailStepKey;
  nextTarget: RailTarget | null;
  nextStepTitle: string | null;
  // Who owns the page title. "page": the card IS the whole page (the surface
  // behind it renders nothing, or the questionnaire steps aside for it), so
  // the card carries the h1. "banner": the step page below draws its own h1
  // ("Prodejní stránka"), and a second one here would give the page two page
  // titles - which is exactly what the third audit round measured on
  // salespage, strategy and handover (29.6 px banner at y 213 over a 38 px
  // heading at y 450). A completion notice is a STATE of the step, not a
  // second name for it.
  variant?: "page" | "banner";
  // F134b: a step can close for a REASON its default sentence does not cover.
  // The feedback step normally ends with "we applied what you learned"; when it
  // ended because the instance changed hands, that is what it has to say.
  benefitKey?: string;
  t: (key: string) => string;
  onContinue: (target: RailTarget) => void;
}) {
  const nextTarget = props.nextTarget;
  const hasNextStep = Boolean(nextTarget && props.nextStepTitle);
  const banner = props.variant === "banner";
  const titleId = `closed-step-${props.step}`;
  const title = props.t(`closedStep.steps.${props.step}.title`);
  return (
    <section
      className={`closed-step-card${banner ? " is-banner" : ""}`}
      data-closed-step={props.step}
      data-closed-step-variant={banner ? "banner" : "page"}
      aria-labelledby={titleId}
    >
      <span className="closed-step-check" aria-hidden="true">✓</span>
      <div className="closed-step-copy">
        {banner
          ? <p className="closed-step-title" id={titleId}>{title}</p>
          : <h1 id={titleId}>{title}</h1>}
        <p className="closed-step-benefit">{props.t(props.benefitKey ?? `closedStep.steps.${props.step}.benefit`)}</p>
        <p>
          {hasNextStep ? (
            <>{props.t("closedStep.nextLead")} <strong>{props.nextStepTitle}</strong>.</>
          ) : props.t("closedStep.journeyComplete")}
        </p>
        {hasNextStep && nextTarget && (
          <button
            className="quiet-link closed-step-next"
            type="button"
            data-target={nextTarget}
            onClick={() => props.onContinue(nextTarget)}
          >
            {props.t("closedStep.continue")}
            <span aria-hidden="true">→</span>
          </button>
        )}
      </div>
    </section>
  );
}

export function LockedStep(props: {
  step: RailStepKey;
  t: (key: string) => string;
  // The padlock has two reasons: the step is still ahead (default copy), or
  // the team is preparing this very step and the client cannot act yet.
  copyKey?: string;
  onBack?: () => void;
  // Where the step sits in THIS client's rail (the app track makes the journey
  // 17 steps instead of 14), so a padlock reads as "later", not as "broken".
  position?: { current: number; total: number };
  // The step that IS on the client's plate now. A locked page without it is a
  // dead end: the only way out used to be the back link in the corner.
  next?: { target: RailTarget; title: string };
  /**
   * F152-W2: `false` says this page is NOT a padlock. A locked step is coming,
   * so it shows the "co tě čeká" card and wears the padlock icon; a step
   * outside this client's journey is never coming, so both would contradict the
   * sentence right beside them ("nic si nemusíš odemykat"), and the card would
   * be an advertisement for work nobody bought.
   */
  preview?: boolean;
  onContinue?: (target: RailTarget) => void;
}) {
  const next = props.next;
  return (
    <section className="locked-step-view" data-locked-step={props.step} aria-labelledby={`locked-step-${props.step}`} role="status">
      {/* The one navigation certainty every other subpage has. A locked page
          is exactly the dead end where it was missing (audit a1-r5-09). */}
      {props.onBack && (
        <button className="surface-back" type="button" onClick={props.onBack}>
          {props.t("common.backHome")}
        </button>
      )}
      <header className="locked-step-heading">
        {props.position && (
          <p className="eyebrow blue-eyebrow">
            {props.t("phase.progress")
              .replace("{current}", String(props.position.current))
              .replace("{total}", String(props.position.total))}
          </p>
        )}
        <h1 id={`locked-step-${props.step}`}>{props.t(`rail.steps.${props.step}`)}</h1>
        <p className={props.preview === false ? "lead" : "lead inline-lock-copy"}>
          {props.preview !== false && <InlineLockIcon />}
          {props.t(props.copyKey ?? `rail.unlock.${props.step}`)}
        </p>
      </header>
      {props.preview !== false && (
        <article className="locked-step-card">
          <LockedStepPreview step={props.step} t={props.t} />
        </article>
      )}
      {next && props.onContinue && (
        <article className="locked-step-next">
          <p>{props.t("rail.lockedNext.lead")} <strong>{next.title}</strong>.</p>
          <button
            className="primary-button"
            type="button"
            data-target={next.target}
            onClick={() => props.onContinue?.(next.target)}
          >
            {props.t("rail.lockedNext.action")}
          </button>
        </article>
      )}
    </section>
  );
}
